import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import { editUser } from '../../Action/api';
import { menuListData } from './menuListData';

const EditUserDialog = ({ open, onClose, onSuccess, userData }) => {
  const [editedUser, setEditedUser] = useState({
    name: '',
    email: '',
    type: 'User',
    permissions: {
      dashboard: false,
      salesboard: false,
      users: false,

      leads: false,
      qualified: false,
      disqualified: false,
      closedWon: false,
      closedLoss: false,
      contacts: false,
      companies: false,
      opportunities: false,
      quotations: false,
      tasks: false,
      meetings: false,
      activities: false,
      reports: false,
      administration: false,
    }
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (userData) {
      setEditedUser({
        name: userData.staffFullName || '',
        email: userData.userId || '',
        type: userData.roleName || 'User',
        permissions: {
          dashboard: userData.menuList?.find(m => m.menuId === 'MENU-001')?.permission === 'yes',
          salesboard: userData.menuList?.find(m => m.menuId === 'MENU-002')?.permission === 'yes',
          users: userData.menuList?.find(m => m.menuId === 'MENU-003')?.permission === 'yes',

          leads: userData.menuList?.find(m => m.menuId === 'MENU-004')?.permission === 'yes',
          qualified: userData.menuList?.find(m => m.menuId === 'MENU-005')?.permission === 'yes',
          disqualified: userData.menuList?.find(m => m.menuId === 'MENU-006')?.permission === 'yes',
          closedWon: userData.menuList?.find(m => m.menuId === 'MENU-007')?.permission === 'yes',
          closedLoss: userData.menuList?.find(m => m.menuId === 'MENU-008')?.permission === 'yes',
          contacts: userData.menuList?.find(m => m.menuId === 'MENU-009')?.permission === 'yes',
          companies: userData.menuList?.find(m => m.menuId === 'MENU-010')?.permission === 'yes',
          opportunities: userData.menuList?.find(m => m.menuId === 'MENU-011')?.permission === 'yes',
          quotations: userData.menuList?.find(m => m.menuId === 'MENU-012')?.permission === 'yes',
          tasks: userData.menuList?.find(m => m.menuId === 'MENU-013')?.permission === 'yes',
          meetings: userData.menuList?.find(m => m.menuId === 'MENU-014')?.permission === 'yes',
          activities: userData.menuList?.find(m => m.menuId === 'MENU-015')?.permission === 'yes',
          reports: userData.menuList?.find(m => m.menuId === 'MENU-016')?.permission === 'yes',
          administration: userData.menuList?.find(m => m.menuId === 'MENU-017')?.permission === 'yes',

        }
      });
    }
  }, [userData]);

  // Update the handleSubmit function with permission validation
  const handleSubmit = async () => {
    // Name validation
    if (!editedUser.name.trim()) {
      setFormError('Full Name is required');
      return;
    }

    // Check if at least one permission is selected
    const hasAnyPermission = Object.values(editedUser.permissions).some(permission => permission);
    if (!hasAnyPermission) {
      setFormError('At least one module permission must be selected');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const requestPayload = {
        userId: editedUser.email,
        staffFullName: editedUser.name.trim(), // Trim any whitespace
        personalEmail: editedUser.email,
        roleName: editedUser.type,
        menuList: [
          {
            menuId: "MENU-001",
            menuName: "Dashboard",
            permission: editedUser.permissions.dashboard ? "yes" : "no"
          },
          {
            menuId: "MENU-002",
            menuName: "Sales Board",
            permission: editedUser.permissions.salesboard ? "yes" : "no"
          },
          {
            menuId: "MENU-003",
            menuName: "Users",
            permission: editedUser.permissions.users ? "yes" : "no"
          },



          {
            menuId: "MENU-004",
            menuName: "Leads",
            permission: editedUser.permissions.leads ? "yes" : "no"
          },
          {
            menuId: "MENU-005",
            menuName: "Qualified",
            permission: editedUser.permissions.qualified ? "yes" : "no"
          },
          {
            menuId: "MENU-006",
            menuName: "Disqualified",
            permission: editedUser.permissions.disqualified ? "yes" : "no"
          },
           {
            menuId: "MENU-007",
            menuName: "Closed Won",
            permission: editedUser.permissions.closedWon ? "yes" : "no"
          },
           {
            menuId: "MENU-008",
            menuName: "Closed Loss",
            permission: editedUser.permissions.closedLoss ? "yes" : "no"
          },
           {
            menuId: "MENU-009",
            menuName: "Contacts",
            permission: editedUser.permissions.contacts ? "yes" : "no"
          },
          {
            menuId: "MENU-010",
            menuName: "Companies",
            permission: editedUser.permissions.companies ? "yes" : "no"
          },
          {
            menuId: "MENU-011",
            menuName: "Opportunities",
            permission: editedUser.permissions.opportunities ? "yes" : "no"
          },
          {
            menuId: "MENU-012",
            menuName: "Quotations",
            permission: editedUser.permissions.quotations ? "yes" : "no"
          },
          {
            menuId: "MENU-013",
            menuName: "Tasks",
            permission: editedUser.permissions.tasks ? "yes" : "no"
          },
          {
            menuId: "MENU-014",
            menuName: "Meetings",
            permission: editedUser.permissions.meetings ? "yes" : "no"
          },
          {
            menuId: "MENU-015",
            menuName: "Activities",
            permission: editedUser.permissions.activities ? "yes" : "no"
          },
          {
            menuId: "MENU-016",
            menuName: "Reports",
            permission: editedUser.permissions.reports ? "yes" : "no"
          },
          {
            menuId: "MENU-017",
            menuName: "Administration",
            permission: editedUser.permissions.administration ? "yes" : "no"
          }
        ]
      };

      const response = await editUser(requestPayload);

      if (response.data && response.data.status) {
        onSuccess(editedUser.name);
        onClose();
      } else {
        setFormError(response.data?.message || 'Failed to update user.');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setFormError('An error occurred while updating the user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermissionChange = (permission) => {
    setEditedUser(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }));
  };

  // Add a helper component to show permission warning
  const PermissionWarning = () => (
    <Typography
      variant="caption"
      sx={{
        color: !Object.values(editedUser.permissions).some(p => p) ? '#dc2626' : 'transparent',
        fontSize: '0.75rem',
        mt: 1,
        display: 'block'
      }}
    >
      * Please select at least one module permission
    </Typography>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
        }
      }}
    >
      <Box sx={{ 
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        pt: 3, 
        pb: 4,
        px: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
        }} />
        <Typography variant="h5" sx={{ 
          color: 'white',
          fontWeight: 700,
          position: 'relative',
          mb: 1
        }}>
          Edit User
        </Typography>
        <Typography variant="body2" sx={{ 
          color: alpha('#fff', 0.7),
          position: 'relative'
        }}>
          Modify user details and permissions
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {formError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
          >
            {formError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Full Name"
              fullWidth
              required
              error={!editedUser.name.trim() && formError}
              helperText={!editedUser.name.trim() && formError ? 'Full Name is required' : ''}
              value={editedUser.name}
              onChange={(e) => setEditedUser(prev => ({ ...prev, name: e.target.value }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="email"
              label="Email Address"
              fullWidth
              required
              disabled
              value={editedUser.email}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={editedUser.type}
                label="User Type"
                onChange={(e) => setEditedUser(prev => ({ ...prev, type: e.target.value }))}
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid',
                borderColor: !Object.values(editedUser.permissions).some(p => p) && formError 
                  ? '#fecaca' 
                  : '#e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha('#3b82f6', 0.1)
                }}>
                  <LockPersonIcon sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#1e293b', mb: 0.5 }}>
                    Module Permissions
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Update accessible modules for this user
                  </Typography>
                </Box>
              </Box>
              <FormGroup>
                {menuListData.map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={editedUser.permissions[permission]}
                        onChange={() => handlePermissionChange(permission)}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked': {
                            color: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem',
                        color: '#475569'
                      }
                    }}
                  />
                ))}
              </FormGroup>
              <PermissionWarning />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          pt: 2,
          borderTop: '1px solid #e2e8f0',
          gap: 1
        }}
      >
        <Button 
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
          sx={{ 
            color: '#64748b',
            borderColor: '#e2e8f0',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            px: 3,
            py: 1
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <span>Updating...</span>
            </Box>
          ) : (
            'Update User'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;